/**
 * Solo Table — default landing screen while lobby/matchmaking are on hold.
 * Creates (or rejoins) the caller's real, server-driven 1-human-vs-bots
 * table via POST /tables/solo, then renders GameTable against the real
 * socket-pushed game state — no more static mock data (see git history for
 * the earlier dev-harness version of this file, which seeded the Zustand
 * store directly and never touched a socket).
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import GameTable from '../components/GameTable/GameTable';
import { gameService, type GameTable as GameTableData } from '../services/gameService';

const SoloTable: React.FC = () => {
  const { token } = useAuth();
  const [tableData, setTableData] = useState<GameTableData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    gameService
      .createSoloTable()
      .then((table) => {
        if (!cancelled) setTableData(table);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to start table');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 text-red-400 p-6 text-center">
        {error}
      </div>
    );
  }

  if (!tableData || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 text-gray-400">
        Setting up your table...
      </div>
    );
  }

  // On a computer the table fits the window (no page scroll).
  return (
    <div className="desk:h-dvh desk:flex desk:flex-col">
      <GameTable tableId={tableData.id} token={token} tableData={tableData} liveSocket={true} />
    </div>
  );
};

export default SoloTable;
